import React, { useState, useEffect, useRef } from 'react';
import { useNavigate as useReactRouterNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTruck,
  faPlane,
  faShip,
  faBox,
  faBuilding,
  faHome,
  faGlobe,
  faWeight,
  faRuler,
  faInfoCircle,
  faTimes,
  faCheck,
  faShippingFast,
  faTruckLoading,
  faGlobeAmericas,
  faPlaneDeparture,
  faHeadset,
  faRedo,
  faCalendarCheck,
  faCalculator,
  faWeightHanging,
  faMapMarkerAlt,
  faDoorOpen,
  faWarehouse,
  faBolt,
  faExclamationCircle,
  faPeopleCarry,
  faIndustry,
  faEdit,
  faSave,
  faPlus,
  faTrash,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';

// Custom navigation hook that works with or without Router context
function useCustomNavigate() {
  try {
    // Try to use the React Router navigate function
    return useReactRouterNavigate();
  } catch (e) {
    // If not in a Router context, provide a fallback navigation function
    return (path) => {
      window.location.href = path;
    };
  }
}

// Pricing configuration that can be updated like a spreadsheet
const initialPricingConfig = {
  express: {
    zones: [
      { id: 1, name: 'ZONE 1 - MAINLAND', baseRate: 2475, weightBrackets: { rate6_10: 2000, rate10Plus: 270 } },
      { id: 2, name: 'ZONE 1 - ISLAND', baseRate: 3475, weightBrackets: { rate6_10: 2000, rate10Plus: 270 } },
      { id: 3, name: 'ZONE 2 - MAINLAND', baseRate: 2975, weightBrackets: { rate6_10: 2000, rate10Plus: 325 } },
      { id: 4, name: 'ZONE 2 - ISLAND', baseRate: 3975, weightBrackets: { rate6_10: 2000, rate10Plus: 270 } },
      { id: 5, name: 'ZONE 3 - MAINLAND', baseRate: 3475, weightBrackets: { rate6_10: 2000, rate10Plus: 270 } },
      { id: 6, name: 'ZONE 4 - MAINLAND', baseRate: 3975, weightBrackets: { rate6_10: 2000, rate10Plus: 270 } },
      { id: 7, name: 'ZONE 5 - ISLAND', baseRate: 4475, weightBrackets: { rate6_10: 2000, rate10Plus: 270 } },
      { id: 8, name: 'ZONE 6 - ISLAND', baseRate: 5475, weightBrackets: { rate6_10: 2000, rate10Plus: 270 } }
    ],
    specialAreas: [],
    baseFee: 0,
    processingFee: 25
  },
  interstate: {
    zones: [
      { id: 1, name: 'Zone 1 (South West)', states: 'Abeokuta, Ado Ekiti, Osogbo, Ibadan (Iwo Road, Challenge, Ring Road), Ilorin, Akure', baseRate: 5099, weightBrackets: { rate6_10: 1476, rate10Plus: 700 } },
      { id: 2, name: 'Zone 2 (South East)', states: 'Aba, Owerri, Enugu, Onitsha, Awka', baseRate: 6599, weightBrackets: { rate6_10: 1376, rate10Plus: 770 } },
      { id: 3, name: 'Zone 3 (South Central)', states: 'Asaba, Warri, Benin', baseRate: 5699, weightBrackets: { rate6_10: 1876, rate10Plus: 870 } },
      { id: 4, name: 'Zone 4 (Central)', states: 'Abuja, Uyo, Port Harcourt, Bayelsa', baseRate: 6699, weightBrackets: { rate6_10: 876, rate10Plus: 870 } },
      { id: 5, name: 'Zone 5 (North Central)', states: 'Kano, Kaduna, Makurdi, Minna, Bauchi', baseRate: 9699, weightBrackets: { rate6_10: 876, rate10Plus: 1170 } }
    ],
    extraWeightCost: 1000,
    doorstepFee: 5000,
    processingFee: 25
  },
  international: {
    zones: [
      { id: 1, name: 'Zone 1', countries: 'UK, Ireland', baseRate: 65000 },
      { id: 2, name: 'Zone 2', countries: 'Ghana, Senegal, Gambia', baseRate: 75000 },
      { id: 3, name: 'Zone 3', countries: 'USA, Canada, Mexico', baseRate: 85000 },
      { id: 4, name: 'Zone 4', countries: 'Cyprus, Germany, France, Italy, Malta, Portugal, Spain, Sweden, Turkey, Austria', baseRate: 92500 },
      { id: 5, name: 'Zone 5', countries: 'Angola, Ethiopia, Kenya, Libya, Namibia, South Africa, Sudan, Tanzania, Zambia', baseRate: 97500 },
      { id: 6, name: 'Zone 6', countries: 'Iraq, Israel, Jordan, Kuwait, Qatar, Saudi Arabia, UAE, Oman', baseRate: 97500 },
      { id: 7, name: 'Zone 7', countries: 'China, India, Japan, Korea, Malaysia, Pakistan, Philippines, Singapore, Vietnam, Maldives, Australia', baseRate: 97500 },
      { id: 8, name: 'Zone 8', countries: 'Argentina, Colombia, Barbados, Brazil, Guyana, New Zealand, Jamaica', baseRate: 97500 }
    ],
    weightBrackets: [
      { id: 1, minWeight: 0, maxWeight: 2, additionalCost: 0 },
      { id: 2, minWeight: 2.1, maxWeight: 5, additionalCost: 5000 },
      { id: 3, minWeight: 5.1, maxWeight: 10, additionalCost: 15000 },
      { id: 4, minWeight: 10.1, maxWeight: null, additionalCostPerKg: 12000 }
    ],
    processingFee: 25
  },
  cargo: {
    countries: [
      { id: 1, name: 'USA', ratePerKg: 16500 },
      { id: 2, name: 'UK', ratePerKg: 9500 },
      { id: 3, name: 'GERMANY', ratePerKg: 7000 },
      { id: 4, name: 'CANADA', ratePerKg: 17000 },
      { id: 5, name: 'OTHER', ratePerKg: 8000 }
    ],
    minWeight: 10,
    processingFee: 25
  },
  haulage: {
    ratePerKg: 1500,
    processingFee: 25
  }
};

const PricingTables = ({ pricingConfig, setPricingConfig }) => {
  const [activeTab, setActiveTab] = useState('express');
  const [editing, setEditing] = useState(null);
  const [newValue, setNewValue] = useState('');

  const handleEdit = (field, id, currentValue) => {
    setEditing(`${field}-${id}`);
    setNewValue(currentValue);
  };

  const handleSave = (field, id) => {
    if (field === 'express-zone-rate') {
      setPricingConfig(prev => ({
        ...prev,
        express: {
          ...prev.express,
          zones: prev.express.zones.map(zone =>
            zone.id === id ? { ...zone, baseRate: Number(newValue) } : zone
          )
        }
      }));
    } else if (field === 'express-special-fee') {
      setPricingConfig(prev => ({
        ...prev,
        express: {
          ...prev.express,
          specialAreas: prev.express.specialAreas.map(area =>
            area.id === id ? { ...area, additionalFee: Number(newValue) } : area
          )
        }
      }));
    } else if (field === 'express-base-fee') {
      setPricingConfig(prev => ({
        ...prev,
        express: {
          ...prev.express,
          baseFee: Number(newValue)
        }
      }));
    } else if (field === 'interstate-zone-rate') {
      setPricingConfig(prev => ({
        ...prev,
        interstate: {
          ...prev.interstate,
          zones: prev.interstate.zones.map(zone =>
            zone.id === id ? { ...zone, baseRate: Number(newValue) } : zone
          )
        }
      }));
    } else if (field === 'interstate-extra-weight') {
      setPricingConfig(prev => ({
        ...prev,
        interstate: {
          ...prev.interstate,
          extraWeightCost: Number(newValue)
        }
      }));
    } else if (field === 'interstate-doorstep') {
      setPricingConfig(prev => ({
        ...prev,
        interstate: {
          ...prev.interstate,
          doorstepFee: Number(newValue)
        }
      }));
    } else if (field === 'international-zone-rate') {
      setPricingConfig(prev => ({
        ...prev,
        international: {
          ...prev.international,
          zones: prev.international.zones.map(zone =>
            zone.id === id ? { ...zone, baseRate: Number(newValue) } : zone
          )
        }
      }));
    } else if (field === 'international-weight-cost') {
      setPricingConfig(prev => ({
        ...prev,
        international: {
          ...prev.international,
          weightBrackets: prev.international.weightBrackets.map(bracket =>
            bracket.id === id ? { ...bracket, additionalCost: Number(newValue) } : bracket
          )
        }
      }));
    } else if (field === 'cargo-rate') {
      setPricingConfig(prev => ({
        ...prev,
        cargo: {
          ...prev.cargo,
          countries: prev.cargo.countries.map(country =>
            country.id === id ? { ...country, ratePerKg: Number(newValue) } : country
          )
        }
      }));
    } else if (field === 'haulage-rate') {
      setPricingConfig(prev => ({
        ...prev,
        haulage: {
          ...prev.haulage,
          ratePerKg: Number(newValue)
        }
      }));
    }

    setEditing(null);
    setNewValue('');
  };

  const renderExpressTable = () => (
    <div className="overflow-x-auto">
      <h3 className="text-lg font-semibold mb-4">Express Pricing (Lagos)</h3>
      <table className="min-w-full bg-white border border-gray-200">
        <thead>
          <tr className="bg-gray-100">
            <th className="py-2 px-4 border-b text-left">Zone</th>
            <th className="py-2 px-4 border-b text-left">Base Rate (₦)</th>
            <th className="py-2 px-4 border-b text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {pricingConfig.express.zones.map(zone => (
            <tr key={zone.id} className="hover:bg-gray-50">
              <td className="py-2 px-4 border-b">{zone.name}</td>
              <td className="py-2 px-4 border-b">
                {editing === `express-zone-rate-${zone.id}` ? (
                  <input
                    type="number"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    className="w-24 px-2 py-1 border rounded"
                    autoFocus
                  />
                ) : (
                  zone.baseRate
                )}
              </td>
              <td className="py-2 px-4 border-b">
                {editing === `express-zone-rate-${zone.id}` ? (
                  <button
                    onClick={() => handleSave('express-zone-rate', zone.id)}
                    className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    <FontAwesomeIcon icon={faSave} />
                  </button>
                ) : (
                  <button
                    onClick={() => handleEdit('express-zone-rate', zone.id, zone.baseRate)}
                    className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                )}
              </td>
            </tr>
          ))}
          <tr className="bg-gray-100 font-semibold">
            <td className="py-2 px-4 border-b">Base Fee</td>
            <td className="py-2 px-4 border-b">
              {editing === 'express-base-fee' ? (
                <input
                  type="number"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  className="w-24 px-2 py-1 border rounded"
                  autoFocus
                />
              ) : (
                pricingConfig.express.baseFee
              )}
            </td>
            <td className="py-2 px-4 border-b">
              {editing === 'express-base-fee' ? (
                <button
                  onClick={() => handleSave('express-base-fee')}
                  className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                >
                  <FontAwesomeIcon icon={faSave} />
                </button>
              ) : (
                <button
                  onClick={() => handleEdit('express-base-fee', null, pricingConfig.express.baseFee)}
                  className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
              )}
            </td>
          </tr>
        </tbody>
      </table>

      <h3 className="text-lg font-semibold mt-6 mb-4">Special Areas Fees</h3>
      <table className="min-w-full bg-white border border-gray-200">
        <thead>
          <tr className="bg-gray-100">
            <th className="py-2 px-4 border-b text-left">Zone</th>
            <th className="py-2 px-4 border-b text-left">Area</th>
            <th className="py-2 px-4 border-b text-left">Additional Fee (₦)</th>
            <th className="py-2 px-4 border-b text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {pricingConfig.express.specialAreas.map(area => (
            <tr key={area.id} className="hover:bg-gray-50">
              <td className="py-2 px-4 border-b">{area.zoneName}</td>
              <td className="py-2 px-4 border-b">{area.areaName}</td>
              <td className="py-2 px-4 border-b">
                {editing === `express-special-fee-${area.id}` ? (
                  <input
                    type="number"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    className="w-24 px-2 py-1 border rounded"
                    autoFocus
                  />
                ) : (
                  area.additionalFee
                )}
              </td>
              <td className="py-2 px-4 border-b">
                {editing === `express-special-fee-${area.id}` ? (
                  <button
                    onClick={() => handleSave('express-special-fee', area.id)}
                    className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    <FontAwesomeIcon icon={faSave} />
                  </button>
                ) : (
                  <button
                    onClick={() => handleEdit('express-special-fee', area.id, area.additionalFee)}
                    className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderInterstateTable = () => (
    <div className="overflow-x-auto">
      <h3 className="text-lg font-semibold mb-4">Inter-state Pricing</h3>
      <table className="min-w-full bg-white border border-gray-200">
        <thead>
          <tr className="bg-gray-100">
            <th className="py-2 px-4 border-b text-left">Zone</th>
            <th className="py-2 px-4 border-b text-left">States</th>
            <th className="py-2 px-4 border-b text-left">Base Rate (₦)</th>
            <th className="py-2 px-4 border-b text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {pricingConfig.interstate.zones.map(zone => (
            <tr key={zone.id} className="hover:bg-gray-50">
              <td className="py-2 px-4 border-b">{zone.name}</td>
              <td className="py-2 px-4 border-b">{zone.states}</td>
              <td className="py-2 px-4 border-b">
                {editing === `interstate-zone-rate-${zone.id}` ? (
                  <input
                    type="number"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    className="w-24 px-2 py-1 border rounded"
                    autoFocus
                  />
                ) : (
                  zone.baseRate
                )}
              </td>
              <td className="py-2 px-4 border-b">
                {editing === `interstate-zone-rate-${zone.id}` ? (
                  <button
                    onClick={() => handleSave('interstate-zone-rate', zone.id)}
                    className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    <FontAwesomeIcon icon={faSave} />
                  </button>
                ) : (
                  <button
                    onClick={() => handleEdit('interstate-zone-rate', zone.id, zone.baseRate)}
                    className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                )}
              </td>
            </tr>
          ))}
          <tr className="bg-gray-100 font-semibold">
            <td className="py-2 px-4 border-b">Extra Weight Cost</td>
            <td className="py-2 px-4 border-b">Per kg above 1kg</td>
            <td className="py-2 px-4 border-b">
              {editing === 'interstate-extra-weight' ? (
                <input
                  type="number"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  className="w-24 px-2 py-1 border rounded"
                  autoFocus
                />
              ) : (
                pricingConfig.interstate.extraWeightCost
              )}
            </td>
            <td className="py-2 px-4 border-b">
              {editing === 'interstate-extra-weight' ? (
                <button
                  onClick={() => handleSave('interstate-extra-weight')}
                  className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                >
                  <FontAwesomeIcon icon={faSave} />
                </button>
              ) : (
                <button
                  onClick={() => handleEdit('interstate-extra-weight', null, pricingConfig.interstate.extraWeightCost)}
                  className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
              )}
            </td>
          </tr>
          <tr className="bg-gray-100 font-semibold">
            <td className="py-2 px-4 border-b">Doorstep Fee</td>
            <td className="py-2 px-4 border-b">For eligible states</td>
            <td className="py-2 px-4 border-b">
              {editing === 'interstate-doorstep' ? (
                <input
                  type="number"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  className="w-24 px-2 py-1 border rounded"
                  autoFocus
                />
              ) : (
                pricingConfig.interstate.doorstepFee
              )}
            </td>
            <td className="py-2 px-4 border-b">
              {editing === 'interstate-doorstep' ? (
                <button
                  onClick={() => handleSave('interstate-doorstep')}
                  className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                >
                  <FontAwesomeIcon icon={faSave} />
                </button>
              ) : (
                <button
                  onClick={() => handleEdit('interstate-doorstep', null, pricingConfig.interstate.doorstepFee)}
                  className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const renderInternationalTable = () => (
    <div className="overflow-x-auto">
      <h3 className="text-lg font-semibold mb-4">International Pricing</h3>
      <table className="min-w-full bg-white border border-gray-200">
        <thead>
          <tr className="bg-gray-100">
            <th className="py-2 px-4 border-b text-left">Zone</th>
            <th className="py-2 px-4 border-b text-left">Countries</th>
            <th className="py-2 px-4 border-b text-left">Base Rate (₦)</th>
            <th className="py-2 px-4 border-b text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {pricingConfig.international.zones.map(zone => (
            <tr key={zone.id} className="hover:bg-gray-50">
              <td className="py-2 px-4 border-b">{zone.name}</td>
              <td className="py-2 px-4 border-b">{zone.countries}</td>
              <td className="py-2 px-4 border-b">
                {editing === `international-zone-rate-${zone.id}` ? (
                  <input
                    type="number"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    className="w-24 px-2 py-1 border rounded"
                    autoFocus
                  />
                ) : (
                  zone.baseRate
                )}
              </td>
              <td className="py-2 px-4 border-b">
                {editing === `international-zone-rate-${zone.id}` ? (
                  <button
                    onClick={() => handleSave('international-zone-rate', zone.id)}
                    className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    <FontAwesomeIcon icon={faSave} />
                  </button>
                ) : (
                  <button
                    onClick={() => handleEdit('international-zone-rate', zone.id, zone.baseRate)}
                    className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="text-lg font-semibold mt-6 mb-4">Weight Brackets</h3>
      <table className="min-w-full bg-white border border-gray-200">
        <thead>
          <tr className="bg-gray-100">
            <th className="py-2 px-4 border-b text-left">Weight Range (kg)</th>
            <th className="py-2 px-4 border-b text-left">Additional Cost (₦)</th>
            <th className="py-2 px-4 border-b text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {pricingConfig.international.weightBrackets.map(bracket => (
            <tr key={bracket.id} className="hover:bg-gray-50">
              <td className="py-2 px-4 border-b">
                {bracket.minWeight} - {bracket.maxWeight || 'Above'}
              </td>
              <td className="py-2 px-4 border-b">
                {editing === `international-weight-cost-${bracket.id}` ? (
                  <input
                    type="number"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    className="w-24 px-2 py-1 border rounded"
                    autoFocus
                  />
                ) : (
                  bracket.additionalCost
                )}
              </td>
              <td className="py-2 px-4 border-b">
                {editing === `international-weight-cost-${bracket.id}` ? (
                  <button
                    onClick={() => handleSave('international-weight-cost', bracket.id)}
                    className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    <FontAwesomeIcon icon={faSave} />
                  </button>
                ) : (
                  <button
                    onClick={() => handleEdit('international-weight-cost', bracket.id, bracket.additionalCost)}
                    className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderCargoTable = () => (
    <div className="overflow-x-auto">
      <h3 className="text-lg font-semibold mb-4">Cargo Pricing</h3>
      <table className="min-w-full bg-white border border-gray-200">
        <thead>
          <tr className="bg-gray-100">
            <th className="py-2 px-4 border-b text-left">Country</th>
            <th className="py-2 px-4 border-b text-left">Rate per kg (₦)</th>
            <th className="py-2 px-4 border-b text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {pricingConfig.cargo.countries.map(country => (
            <tr key={country.id} className="hover:bg-gray-50">
              <td className="py-2 px-4 border-b">{country.name}</td>
              <td className="py-2 px-4 border-b">
                {editing === `cargo-rate-${country.id}` ? (
                  <input
                    type="number"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    className="w-24 px-2 py-1 border rounded"
                    autoFocus
                  />
                ) : (
                  country.ratePerKg
                )}
              </td>
              <td className="py-2 px-4 border-b">
                {editing === `cargo-rate-${country.id}` ? (
                  <button
                    onClick={() => handleSave('cargo-rate', country.id)}
                    className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    <FontAwesomeIcon icon={faSave} />
                  </button>
                ) : (
                  <button
                    onClick={() => handleEdit('cargo-rate', country.id, country.ratePerKg)}
                    className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                )}
              </td>
            </tr>
          ))}
          <tr className="bg-gray-100 font-semibold">
            <td className="py-2 px-4 border-b">Minimum Weight</td>
            <td className="py-2 px-4 border-b">{pricingConfig.cargo.minWeight} kg</td>
            <td className="py-2 px-4 border-b"></td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const renderHaulageTable = () => (
    <div className="overflow-x-auto">
      <h3 className="text-lg font-semibold mb-4">Haulage Pricing</h3>
      <table className="min-w-full bg-white border border-gray-200">
        <thead>
          <tr className="bg-gray-100">
            <th className="py-2 px-4 border-b text-left">Parameter</th>
            <th className="py-2 px-4 border-b text-left">Value</th>
            <th className="py-2 px-4 border-b text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr className="hover:bg-gray-50">
            <td className="py-2 px-4 border-b">Rate per kg</td>
            <td className="py-2 px-4 border-b">
              {editing === 'haulage-rate' ? (
                <input
                  type="number"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  className="w-24 px-2 py-1 border rounded"
                  autoFocus
                />
              ) : (
                pricingConfig.haulage.ratePerKg
              )}
            </td>
            <td className="py-2 px-4 border-b">
              {editing === 'haulage-rate' ? (
                <button
                  onClick={() => handleSave('haulage-rate')}
                  className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                >
                  <FontAwesomeIcon icon={faSave} />
                </button>
              ) : (
                <button
                  onClick={() => handleEdit('haulage-rate', null, pricingConfig.haulage.ratePerKg)}
                  className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Pricing Configuration</h2>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {['express', 'interstate', 'international', 'cargo', 'haulage'].map(tab => (
            <button
              key={tab}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-6">
        {activeTab === 'express' && renderExpressTable()}
        {activeTab === 'interstate' && renderInterstateTable()}
        {activeTab === 'international' && renderInternationalTable()}
        {activeTab === 'cargo' && renderCargoTable()}
        {activeTab === 'haulage' && renderHaulageTable()}
      </div>

      <div className="mt-8 p-4 bg-blue-50 rounded-lg">
        <h3 className="font-bold text-lg mb-2 text-blue-800">Processing Fees</h3>
        <p className="text-gray-700">
          All shipments include a processing fee of ₦{pricingConfig.express.processingFee}
        </p>
      </div>
    </div>
  );
};

const Quote = () => {
  const navigate = useCustomNavigate();
  const [formData, setFormData] = useState({
    shipmentType: '', // 'express', 'interstate', 'international', 'cargo', or 'haulage'
    destination: '',
    weight: '',
    deliveryType: 'pickup', // 'pickup' or 'doorstep'
    expressService: false
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null);
  const [showQuote, setShowQuote] = useState(false);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [destinationError, setDestinationError] = useState('');
  const [showPricingConfig, setShowPricingConfig] = useState(false);
  const [pricingConfig, setPricingConfig] = useState(initialPricingConfig);

  const expressDestinationRef = useRef(null);
  const interstateDestinationRef = useRef(null);
  const internationalDestinationRef = useRef(null);
  const cargoDestinationRef = useRef(null);
  const debounceTimeout = useRef(null);

  const nigerianStates = [
    'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
    'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT - Abuja', 'Gombe',
    'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos',
    'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto',
    'Taraba', 'Yobe', 'Zamfara'
  ];

  // Flat list of all Lagos areas for express autocomplete
  const lagosAreas = [
    // ZONE 1 - MAINLAND
    'Surulere', 'Opebi', 'Anthony', 'Ejigbo', 'Isolo', 'Iyana Itire', 'Ikeja', 'Computer village Ikeja', 'Arena market Oshodi', 'Oshodi', 'Obanikoro', 'Mushin', 'Palmgrove', 'Onipanu', 'Ago palace', 'Ilupeju', 'Jakande gate', 'Gbagada', 'Jibowu', 'Fadeyi', 'Maryland', 'ojota', 'Festac', 'Amuwo odofin', 'Oworonshoki', 'Bariga', 'Yaba', 'Shomolu',
    // ZONE 2 - MAINLAND
    'Ikotun', 'Idimu', 'Ijegun', 'Isheri oshun', 'Costain', 'Egbeda', 'Gowon estate', 'Akowonjo', 'Cele egbe', 'Agege', 'Ketu', 'Alapere', 'Ebute metta', 'Ogba', 'Abule egba', 'Igando', 'Omole phase 1/2', 'Magodo', 'Ogudu', 'Iyana ipaja', 'Dopemu', 'Ifako ijaiye', 'Iju ishaga', 'Meiran', 'Alagbado', 'Ayobo', 'Alakuko',
    // ZONE 3 - MAINLAND
    'Mile 2', 'Trade fair', 'Alaba', 'Maza Maza', 'Abule ado', 'Orile', 'Ojo', 'Iba', 'Satellite town', 'Navy town', 'Ajegunle', 'Apapa',
    // ZONE 4 - MAINLAND
    'Mowe', 'Opic', 'Ojodu Berger', 'Ikorodu', 'Ijainkin', 'Iyana era', 'Sangotedo', 'Awoyaya', 'Lakowe',
    // ZONE 5 - ISLAND
    'Banana island', 'Ikoyi', 'Eko', 'Oniru', 'Admiralty Lekki', 'Victoria Island (Vi', 'Lagos Island', 'CMS', 'Idumota', 'Marwa Lekki', 'Lekki Phase 1', 'Lekki phase 2', 'Jakande lekki', 'Osapa London', 'Alpha beach Lekki', 'Orchid Rd Lekki', 'Ilasan Lekki', 'Chevron Lekki', 'VGC', 'Ikota Lekki', 'Agungi', 'Ologolo', 'Igbofon',
    // ZONE 6 - ISLAND
    'Ajah', 'Abraham adensanya', 'Thomas estate', 'LBS', 'Lamgbasa', 'Badore ajah', 'Ogombo', 'Addo',
  ];

  // Inter-state zones and states
  const interStateZones = {
    1: ['Abeokuta', 'Ado Ekiti', 'Osogbo', 'Ibadan', 'Ilorin', 'Akure'],
    2: ['Aba', 'Owerri', 'Enugu', 'Onitsha', 'Awka'],
    3: ['Asaba', 'Warri', 'Benin'],
    4: ['Abuja', 'Uyo', 'Port Harcourt', 'Bayelsa'],
    5: ['Kano', 'Kaduna', 'Makurdi', 'Minna', 'Bauchi']
  };

  // Create flat list of inter-state destinations
  const interStateDestinations = [];
  for (const states of Object.values(interStateZones)) {
    interStateDestinations.push(...states);
  }

  // States with doorstep delivery availability (All supported interstate destinations now)
  const doorstepAvailableStates = [...interStateDestinations];

  // Create international destinations list from zone mapping
  const internationalDestinations = [];
  pricingConfig.international.zones.forEach(zone => {
    internationalDestinations.push(...zone.countries.split(', ').map(c => c.trim()));
  });

  // Cargo destinations (limited to specific countries)
  const cargoDestinations = pricingConfig.cargo.countries.map(c => c.name);

  // Haulage/Relocation destinations (Nigerian states)
  const haulageDestinations = nigerianStates;

  // Shipment types with icons
  const shipmentTypes = [
    {
      value: 'express',
      label: 'Express',
      description: 'Within Lagos',
      icon: faShippingFast
    },
    {
      value: 'interstate',
      label: 'Inter-state',
      description: 'Lagos to other states',
      icon: faTruckLoading
    },
    {
      value: 'international',
      label: 'International',
      description: 'Global shipping',
      icon: faGlobeAmericas
    },
    {
      value: 'cargo',
      label: 'Cargo',
      description: 'Heavy freight',
      icon: faPlaneDeparture
    },
    {
      value: 'haulage',
      label: 'Haulage',
      description: 'Relocation',
      icon: faIndustry
    }
  ];

  // Function to get zone by country for international
  const getZoneByCountry = (country) => {
    const countryUpper = country.toUpperCase();
    for (const zone of pricingConfig.international.zones) {
      if (zone.countries.toUpperCase().includes(countryUpper)) {
        return zone.id;
      }
    }
    return null;
  };

  // Function to get zone by state for inter-state
  const getZoneByState = (state) => {
    const stateUpper = state.toUpperCase();
    for (const [zoneId, states] of Object.entries(interStateZones)) {
      if (states.some(s => s.toUpperCase() === stateUpper)) {
        return parseInt(zoneId);
      }
    }
    return null;
  };

  // Function to get Lagos area rate
  const getLagosAreaRate = (area) => {
    const areaUpper = area.toUpperCase();

    // Map areas to zones manually based on the new list
    const zoneMapping = {
      'ZONE 1': ['Surulere', 'Opebi', 'Anthony', 'Ejigbo', 'Isolo', 'Iyana Itire', 'Ikeja', 'Computer village Ikeja', 'Arena market Oshodi', 'Oshodi', 'Obanikoro', 'Mushin', 'Palmgrove', 'Onipanu', 'Ago palace', 'Ilupeju', 'Jakande gate', 'Gbagada', 'Jibowu', 'Fadeyi', 'Maryland', 'ojota', 'Festac', 'Amuwo odofin', 'Oworonshoki', 'Bariga', 'Yaba', 'Shomolu'],
      'ZONE 2': ['Ikotun', 'Idimu', 'Ijegun', 'Isheri oshun', 'Costain', 'Egbeda', 'Gowon estate', 'Akowonjo', 'Cele egbe', 'Agege', 'Ketu', 'Alapere', 'Ebute metta', 'Ogba', 'Abule egba', 'Igando', 'Omole phase 1/2', 'Magodo', 'Ogudu', 'Iyana ipaja', 'Dopemu', 'Ifako ijaiye', 'Iju ishaga', 'Meiran', 'Alagbado', 'Ayobo', 'Alakuko'],
      'ZONE 3': ['Mile 2', 'Trade fair', 'Alaba', 'Maza Maza', 'Abule ado', 'Orile', 'Ojo', 'Iba', 'Satellite town', 'Navy town', 'Ajegunle', 'Apapa'],
      'ZONE 4': ['Mowe', 'Opic', 'Ojodu Berger', 'Ikorodu', 'Ijainkin', 'Iyana era', 'Sangotedo', 'Awoyaya', 'Lakowe'],
      'ZONE 5': ['Banana island', 'Ikoyi', 'Eko', 'Oniru', 'Admiralty Lekki', 'Victoria Island (Vi', 'Lagos Island', 'CMS', 'Idumota', 'Marwa Lekki', 'Lekki Phase 1', 'Lekki phase 2', 'Jakande lekki', 'Osapa London', 'Alpha beach Lekki', 'Orchid Rd Lekki', 'Ilasan Lekki', 'Chevron Lekki', 'VGC', 'Ikota Lekki', 'Agungi', 'Ologolo', 'Igbofon'],
      'ZONE 6': ['Ajah', 'Abraham adensanya', 'Thomas estate', 'LBS', 'Lamgbasa', 'Badore ajah', 'Ogombo', 'Addo']
    };

    // Map shorthand zone keys to the full config zone names
    const zoneNameMap = {
      'ZONE 1': 'ZONE 1 - MAINLAND',
      'ZONE 2': 'ZONE 2 - MAINLAND',
      'ZONE 3': 'ZONE 3 - MAINLAND',
      'ZONE 4': 'ZONE 4 - MAINLAND',
      'ZONE 5': 'ZONE 5 - ISLAND',
      'ZONE 6': 'ZONE 6 - ISLAND',
    };

    for (const [zoneName, areas] of Object.entries(zoneMapping)) {
      if (areas.some(a => a.toUpperCase() === areaUpper)) {
        const fullZoneName = zoneNameMap[zoneName] || zoneName;
        const zone = pricingConfig.express.zones.find(z => z.name === fullZoneName);
        if (zone) {
          return {
            baseRate: zone.baseRate,
            additionalFee: 0,
            weightBrackets: zone.weightBrackets
          };
        }
      }
    }

    return null;
  };

  // Function to filter and sort destinations by relevance
  const filterAndSortDestinations = (input, destinations, type) => {
    if (!input || input.trim() === '') return [];

    const inputUpper = input.toUpperCase();

    // Score each destination based on relevance
    const scoredDestinations = destinations.map(dest => {
      const destUpper = dest.toUpperCase();
      let score = 0;

      // Exact match gets highest score
      if (destUpper === inputUpper) {
        score = 100;
      }
      // Starts with input gets high score
      else if (destUpper.startsWith(inputUpper)) {
        score = 90 - destUpper.length; // Shorter matches get higher score
      }
      // Contains input gets lower score
      else if (destUpper.includes(inputUpper)) {
        score = 50 - destUpper.length;
      }

      return {
        name: dest,
        score,
        type
      };
    });

    // Filter out zero scores and sort by score (descending)
    return scoredDestinations
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10); // Limit to top 10 results
  };

  const expressServices = [
    { value: 'local-delivery', label: 'Local Delivery', icon: faTruck },
    { value: 'interstate', label: 'Interstate Delivery', icon: faTruck },
    { value: 'ecommerce', label: 'E-commerce', icon: faBox },
    { value: 'b2b-logistics', label: 'B2B Logistics', icon: faBuilding }
  ];

  const internationalServices = [
    { value: 'express-international', label: 'Express International', icon: faPlane },
    { value: 'cargo-shipping', label: 'Cargo Shipping', icon: faShip },
    { value: 'air-sea-freight', label: 'Air/Sea Freight', icon: faPlane }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Handle destination input for autocomplete with debounce
    if (name === 'destination') {
      setDestinationError('');

      if (value.trim() === '') {
        setDestinationSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      // Clear previous timeout
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      // Set new timeout for debounce
      debounceTimeout.current = setTimeout(() => {
        if (formData.shipmentType === 'express') {
          const suggestions = filterAndSortDestinations(value, lagosAreas, 'Area');
          setDestinationSuggestions(suggestions);
          setShowSuggestions(true);
        } else if (formData.shipmentType === 'interstate') {
          const suggestions = filterAndSortDestinations(value, interStateDestinations, 'State');
          setDestinationSuggestions(suggestions);
          setShowSuggestions(true);
        } else if (formData.shipmentType === 'international') {
          const suggestions = filterAndSortDestinations(value, internationalDestinations, 'Country');
          setDestinationSuggestions(suggestions);
          setShowSuggestions(true);
        } else if (formData.shipmentType === 'cargo') {
          const suggestions = filterAndSortDestinations(value, cargoDestinations, 'Country');
          setDestinationSuggestions(suggestions);
          setShowSuggestions(true);
        } else if (formData.shipmentType === 'haulage') {
          const suggestions = filterAndSortDestinations(value, haulageDestinations, 'State');
          setDestinationSuggestions(suggestions);
          setShowSuggestions(true);
        }
      }, 300); // 300ms debounce delay
    }
  };

  const handleDestinationSelect = (destination) => {
    setFormData(prev => ({
      ...prev,
      destination: destination.name
    }));
    setShowSuggestions(false);
    setDestinationError('');
  };

  const handleClickOutside = (e) => {
    if (
      expressDestinationRef.current && !expressDestinationRef.current.contains(e.target) &&
      interstateDestinationRef.current && !interstateDestinationRef.current.contains(e.target) &&
      internationalDestinationRef.current && !internationalDestinationRef.current.contains(e.target) &&
      cargoDestinationRef.current && !cargoDestinationRef.current.contains(e.target)
    ) {
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Clear debounce timeout on unmount
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Calculate quote based on form data
    let basePrice = 0;

    if (formData.shipmentType === 'express') {
      // Express shipment pricing (within Lagos)
      const areaRate = getLagosAreaRate(formData.destination);
      if (areaRate === null) {
        setDestinationError('Destination unavailable');
        return;
      }

      const weight = parseFloat(formData.weight) || 0;
      const weightBrackets = areaRate.weightBrackets || { rate6_10: 0, rate10Plus: 0 };

      let weightFee = 0;
      if (weight <= 5) {
        weightFee = 0;
      } else if (weight <= 10) {
        weightFee = weightBrackets.rate6_10;
      } else {
        weightFee = weightBrackets.rate6_10 + (weight - 10) * weightBrackets.rate10Plus;
      }

      // Calculate base cost
      let baseCost = areaRate.baseRate + areaRate.additionalFee + weightFee + pricingConfig.express.baseFee;

      // Add processing fee
      baseCost += pricingConfig.express.processingFee;

      basePrice = baseCost;
    } else if (formData.shipmentType === 'interstate') {
      // Inter-state shipment pricing (from Lagos to other states)
      const zoneId = getZoneByState(formData.destination);
      if (zoneId === null) {
        setDestinationError('Destination unavailable');
        return;
      }

      const zone = pricingConfig.interstate.zones.find(z => z.id === zoneId);
      if (!zone) {
        setDestinationError('Destination unavailable');
        return;
      }

      const weight = parseFloat(formData.weight) || 0;
      const weightBrackets = zone.weightBrackets || { rate6_10: 0, rate10Plus: 0 };

      // Base cost for up to 5kg
      let baseCost = zone.baseRate;

      // Add tiered weight cost
      let weightFee = 0;
      if (weight <= 5) {
        weightFee = 0;
      } else if (weight <= 10) {
        weightFee = weightBrackets.rate6_10;
      } else {
        weightFee = weightBrackets.rate6_10 + (weight - 10) * weightBrackets.rate10Plus;
      }

      baseCost += weightFee;

      // Add doorstep delivery cost if selected
      if (formData.deliveryType === 'doorstep') {
        baseCost += pricingConfig.interstate.doorstepFee;
      }

      // Add processing fee
      baseCost += pricingConfig.interstate.processingFee;

      basePrice = baseCost;
    } else if (formData.shipmentType === 'international') {
      // International shipment pricing
      const zoneId = getZoneByCountry(formData.destination);
      if (zoneId === null) {
        setDestinationError('Destination unavailable');
        return;
      }

      const zone = pricingConfig.international.zones.find(z => z.id === zoneId);
      if (!zone) {
        setDestinationError('Destination unavailable');
        return;
      }

      const weight = parseFloat(formData.weight);
      let baseCost = zone.baseRate;

      // Find applicable weight bracket
      for (const bracket of pricingConfig.international.weightBrackets) {
        if (weight >= bracket.minWeight && (bracket.maxWeight === null || weight <= bracket.maxWeight)) {
          baseCost += bracket.additionalCost;
          break;
        }
      }

      // Add extra cost for weight above 10kg
      if (weight > 10) {
        const extraWeight = weight - 10;
        const extraBracket = pricingConfig.international.weightBrackets.find(b => b.id === 4);
        if (extraBracket) {
          baseCost += extraWeight * extraBracket.additionalCostPerKg;
        }
      }

      // Add processing fee
      baseCost += pricingConfig.international.processingFee;
      basePrice = baseCost;
    } else if (formData.shipmentType === 'cargo') {
      // Cargo shipment pricing (minimum 10kg)
      const weight = parseFloat(formData.weight);

      if (weight < pricingConfig.cargo.minWeight) {
        setDestinationError(`Minimum weight for cargo shipment is ${pricingConfig.cargo.minWeight}kg`);
        return;
      }

      // Find country rate
      const dest = formData.destination.toUpperCase();
      let country = pricingConfig.cargo.countries.find(
        c => c.name.toUpperCase() === dest
      );

      // Handle synonyms
      if (!country) {
        if (dest === 'UNITED KINGDOM') {
          country = pricingConfig.cargo.countries.find(c => c.name === 'UK');
        } else if (dest === 'UNITED STATES' || dest === 'UNITED STATES OF AMERICA') {
          country = pricingConfig.cargo.countries.find(c => c.name === 'USA');
        }
      }

      if (!country) {
        // Use "OTHER" rate if country not found
        const otherCountry = pricingConfig.cargo.countries.find(c => c.name === 'OTHER');
        basePrice = weight * (otherCountry ? otherCountry.ratePerKg : 8000);
      } else {
        basePrice = weight * country.ratePerKg;
      }

      // Add processing fee
      basePrice += pricingConfig.cargo.processingFee;
    } else if (formData.shipmentType === 'haulage') {
      // Haulage/Relocation pricing (within Nigeria)
      const weight = parseFloat(formData.weight);

      // Base rate for haulage (per kg)
      basePrice = weight * pricingConfig.haulage.ratePerKg;

      // Add processing fee
      basePrice += pricingConfig.haulage.processingFee;
    }

    // Set quote result
    setQuoteResult({
      price: Math.round(basePrice),
      currency: 'NGN'
    });

    setShowQuote(true);
    setIsSubmitted(true);

    // Reset form after submission
    setTimeout(() => {
      setIsSubmitted(false);
    }, 5000);
  };

  const resetForm = () => {
    setFormData({
      shipmentType: '',
      destination: '',
      weight: '',
      deliveryType: 'pickup',
      expressService: false
    });
    setShowQuote(false);
    setQuoteResult(null);
    setDestinationError('');
  };

  const handleBookShipment = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Log to console for debugging
    console.log("Navigating to signup page");

    // Navigate to signup page using our custom navigate function
    navigate('/auth/signup');
  };

  const renderServiceInfo = () => {
    if (formData.shipmentType === 'express') {
      return (
        <div className="bg-white p-4 rounded-lg border border-red-200">
          <h3 className="font-bold text-lg mb-2 text-red-800 flex items-center gap-2">
            <FontAwesomeIcon icon={faShippingFast} /> Express Delivery Notes
          </h3>
          <ul className="text-sm text-gray-700 space-y-2">
            <li>• Additional fee if package is bulky</li>
            <li>• Lagos zone rate alone</li>
            <li>• ORDER BEFORE 12PM FOR EARLY PICK-UP</li>
            <li>• 24hours delivery service</li>
            <li>• RESCHEDULE DELIVERY FOR NEXT DAY PICK-UP</li>
            <li className="font-semibold mt-2">Customer service: 07046674299</li>
          </ul>
        </div>
      );
    } else if (formData.shipmentType === 'interstate') {
      return (
        <div className="bg-white p-4 rounded-lg border border-red-200">
          <h3 className="font-bold text-lg mb-2 text-red-800 flex items-center gap-2">
            <FontAwesomeIcon icon={faTruckLoading} /> Inter-state Shipping
          </h3>
          <p className="text-sm text-gray-700 mb-3">
            Delivery from Lagos to other Nigerian states.
          </p>
        </div>
      );
    } else if (formData.shipmentType === 'international') {
      return (
        <div className="bg-white p-4 rounded-lg border border-red-200">
          <h3 className="font-bold text-lg mb-2 text-red-800 flex items-center gap-2">
            <FontAwesomeIcon icon={faGlobeAmericas} /> International Service Info
          </h3>
          <div className="space-y-4">
            <div className="p-3 bg-red-50 rounded-lg border border-red-100">
              <h4 className="font-bold text-red-800 text-sm mb-1 flex items-center gap-2">
                <FontAwesomeIcon icon={faShippingFast} /> Express Shipping
              </h4>
              <p className="text-xs text-gray-700">Fast international door-to-door delivery. Estimated time: 3-5 business days. Volumetric weight applies.</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <h4 className="font-bold text-blue-800 text-sm mb-1 flex items-center gap-2">
                <FontAwesomeIcon icon={faBox} /> Cargo Shipping
              </h4>
              <p className="text-xs text-gray-700">Economy cargo service for heavier shipments. Minimum weight: 10kg.</p>
            </div>
          </div>
          <p className="text-[10px] text-gray-500 mt-4 italic">Customer service: 07046674299</p>
        </div>
      );
    } else if (formData.shipmentType === 'cargo') {
      return (
        <div className="bg-white p-4 rounded-lg border border-red-200">
          <h3 className="font-bold text-lg mb-2 text-red-800 flex items-center gap-2">
            <FontAwesomeIcon icon={faPlaneDeparture} /> Cargo Shipping
          </h3>
          <ul className="text-sm text-gray-700 space-y-2">
            <li>• Minimum weight: {pricingConfig.cargo.minWeight}kg</li>
            <li>• Available destinations: {cargoDestinations.join(', ')}</li>
            <li>• Special handling for heavy items</li>
            <li>• Door-to-door delivery service</li>
            <li className="font-semibold mt-2">Customer service: 07046674299</li>
          </ul>
        </div>
      );
    } else if (formData.shipmentType === 'haulage') {
      return (
        <div className="bg-white p-4 rounded-lg border border-red-200">
          <h3 className="font-bold text-lg mb-2 text-red-800 flex items-center gap-2">
            <FontAwesomeIcon icon={faPeopleCarry} /> Haulage & Relocation
          </h3>
          <ul className="text-sm text-gray-700 space-y-2">
            <li>• Office and home relocation services</li>
            <li>• Heavy equipment transportation</li>
            <li>• Available across all Nigerian states</li>
            <li>• Professional handling and setup</li>
            <li className="font-semibold mt-2">Customer service: 07046674299</li>
          </ul>
        </div>
      );
    } else {
      return (
        <div className="text-center py-8">
          <FontAwesomeIcon icon={faInfoCircle} className="text-red-600 text-4xl mb-4 opacity-50" />
          <p className="text-white text-lg">
            Select a shipment type to see available services and pricing information.
          </p>
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-600 to-red-800 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"
        }}></div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">Request a Quote</h1>
          <p className="text-xl max-w-2xl mx-auto opacity-90">
            Get a customized quote for your logistics needs. Select between Express, Inter-state, International, Cargo or Haulage shipment.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-16">
        <div className="max-w-4xl mx-auto">
          {showPricingConfig ? (
            <PricingTables
              pricingConfig={pricingConfig}
              setPricingConfig={setPricingConfig}
            />
          ) : isSubmitted && showQuote ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center mb-8 shadow-lg animate-fadeIn">
              <div className="text-green-600 text-5xl mb-4 animate-scaleIn">
                <FontAwesomeIcon icon={faCheck} />
              </div>
              <h2 className="text-2xl font-bold text-green-800 mb-4">Quote Generated!</h2>
              <div className="bg-white p-6 rounded-lg shadow-md max-w-md mx-auto mb-6">
                <p className="text-gray-700 mb-2 text-lg">Estimated Cost:</p>
                <p className="text-3xl font-bold text-green-700 mb-4 p-4 bg-green-50 rounded-lg">
                  {quoteResult.currency} {quoteResult.price.toLocaleString()}
                </p>
              </div>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={resetForm}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all hover:shadow-md flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faRedo} /> New Quote
                </button>
                <button
                  onClick={handleBookShipment}
                  type="button"
                  className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all hover:shadow-md flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faCalendarCheck} /> Book Shipment
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
              <div className="md:flex">
                {/* Form Section */}
                <div className="md:w-2/3 p-8">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800 relative pb-3">
                      Shipment Details
                      <span className="absolute bottom-0 left-0 w-16 h-1 bg-red-600 rounded"></span>
                    </h2>
                    {/* Pricing Config button removed as requested */}
                  </div>

                  <form onSubmit={handleSubmit}>
                    {/* Shipment Type Selection */}
                    <div className="mb-8">
                      <label className="block text-gray-700 mb-3 font-semibold">Select Shipment Type</label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
                        {shipmentTypes.map((type) => (
                          <div
                            key={type.value}
                            className={`border-2 rounded-lg p-3 md:p-4 cursor-pointer transition-all duration-300 bg-white relative overflow-hidden flex flex-col items-center justify-center h-32 md:h-40 ${formData.shipmentType === type.value
                              ? 'border-red-600 bg-red-50 shadow-md transform -translate-y-1'
                              : 'border-gray-300 hover:border-red-400 hover:shadow-md hover:transform hover:-translate-y-1'
                              }`}
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              shipmentType: type.value,
                              destination: '',
                              deliveryType: 'pickup',
                              expressService: false
                            }))}
                          >
                            <div className={`absolute top-0 left-0 right-0 h-1 bg-red-600 transform transition-transform duration-300 ${formData.shipmentType === type.value ? 'scale-x-100' : 'scale-x-0'
                              }`}></div>
                            <div className="w-10 h-10 md:w-16 md:h-16 bg-red-50 rounded-full flex items-center justify-center mb-2 md:mb-3 transition-colors">
                              <FontAwesomeIcon icon={type.icon} className="text-red-600 text-lg md:text-2xl" />
                            </div>
                            <h3 className="font-semibold text-gray-800 text-sm md:text-base mb-1 text-center">{type.label}</h3>
                            <p className="text-xs md:text-sm text-gray-600 text-center">{type.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {formData.shipmentType && (
                      <React.Fragment>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                          <div>
                            <label htmlFor="weight" className="block text-gray-700 mb-2 font-semibold flex items-center gap-2">
                              <FontAwesomeIcon icon={faWeightHanging} /> Weight (kg)
                            </label>
                            <input
                              type="number"
                              id="weight"
                              name="weight"
                              value={formData.weight}
                              onChange={handleChange}
                              step="0.1"
                              min={formData.shipmentType === 'cargo' ? pricingConfig.cargo.minWeight : "0.1"}
                              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all"
                              required
                            />
                            {formData.shipmentType === 'cargo' && (
                              <p className="text-xs text-gray-500 mt-1">Minimum weight: {pricingConfig.cargo.minWeight}kg</p>
                            )}
                          </div>

                          {formData.shipmentType === 'express' && (
                            <div className="relative" ref={expressDestinationRef}>
                              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700 bg-red-50 px-3 py-2 rounded-lg border border-red-100">
                                <span className="text-red-600 font-bold">Lagos</span>
                                <FontAwesomeIcon icon={faArrowRight} className="text-red-500" />
                                <span className="text-gray-600">Destination Area</span>
                              </div>
                              <label htmlFor="destination" className="block text-gray-700 mb-2 font-semibold flex items-center gap-2">
                                <FontAwesomeIcon icon={faMapMarkerAlt} /> Destination Area
                              </label>
                              <input
                                type="text"
                                id="destination"
                                name="destination"
                                value={formData.destination}
                                onChange={handleChange}
                                onFocus={() => formData.destination && setShowSuggestions(true)}
                                placeholder="Start typing area name..."
                                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all ${destinationError ? 'border-red-500' : 'border-gray-300'
                                  }`}
                                required
                              />
                              {destinationError && (
                                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                  <FontAwesomeIcon icon={faExclamationCircle} /> {destinationError}
                                </p>
                              )}
                              {showSuggestions && destinationSuggestions.length > 0 && (
                                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 shadow-lg max-h-60 overflow-y-auto animate-slideDown">
                                  {destinationSuggestions.map((suggestion, index) => (
                                    <div
                                      key={index}
                                      className="px-4 py-3 hover:bg-red-50 cursor-pointer flex justify-between items-center"
                                      onClick={() => handleDestinationSelect(suggestion)}
                                    >
                                      <span>{suggestion.name}</span>
                                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                        {suggestion.type}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {formData.shipmentType === 'interstate' && (
                            <div className="relative" ref={interstateDestinationRef}>
                              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700 bg-red-50 px-3 py-2 rounded-lg border border-red-100">
                                <span className="text-red-600 font-bold">Lagos</span>
                                <FontAwesomeIcon icon={faArrowRight} className="text-red-500" />
                                <span className="text-gray-600">Destination State</span>
                              </div>
                              <label htmlFor="destination" className="block text-gray-700 mb-2 font-semibold flex items-center gap-2">
                                <FontAwesomeIcon icon={faMapMarkerAlt} /> Destination State
                              </label>
                              <input
                                type="text"
                                id="destination"
                                name="destination"
                                value={formData.destination}
                                onChange={handleChange}
                                onFocus={() => formData.destination && setShowSuggestions(true)}
                                placeholder="Start typing state name..."
                                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all ${destinationError ? 'border-red-500' : 'border-gray-300'
                                  }`}
                                required
                              />
                              {destinationError && (
                                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                  <FontAwesomeIcon icon={faExclamationCircle} /> {destinationError}
                                </p>
                              )}
                              {showSuggestions && destinationSuggestions.length > 0 && (
                                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 shadow-lg max-h-60 overflow-y-auto animate-slideDown">
                                  {destinationSuggestions.map((suggestion, index) => (
                                    <div
                                      key={index}
                                      className="px-4 py-3 hover:bg-red-50 cursor-pointer flex justify-between items-center"
                                      onClick={() => handleDestinationSelect(suggestion)}
                                    >
                                      <span>{suggestion.name}</span>
                                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                        {suggestion.type}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {formData.shipmentType === 'international' && (
                            <div className="relative" ref={internationalDestinationRef}>
                              <label htmlFor="destination" className="block text-gray-700 mb-2 font-semibold flex items-center gap-2">
                                <FontAwesomeIcon icon={faMapMarkerAlt} /> Destination
                              </label>
                              <input
                                type="text"
                                id="destination"
                                name="destination"
                                value={formData.destination}
                                onChange={handleChange}
                                onFocus={() => formData.destination && setShowSuggestions(true)}
                                placeholder="Start typing country name..."
                                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all ${destinationError ? 'border-red-500' : 'border-gray-300'
                                  }`}
                                required
                              />
                              {destinationError && (
                                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                  <FontAwesomeIcon icon={faExclamationCircle} /> {destinationError}
                                </p>
                              )}
                              {showSuggestions && destinationSuggestions.length > 0 && (
                                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 shadow-lg max-h-60 overflow-y-auto animate-slideDown">
                                  {destinationSuggestions.map((suggestion, index) => (
                                    <div
                                      key={index}
                                      className="px-4 py-3 hover:bg-red-50 cursor-pointer flex justify-between items-center"
                                      onClick={() => handleDestinationSelect(suggestion)}
                                    >
                                      <span>{suggestion.name}</span>
                                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                        {suggestion.type}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {formData.shipmentType === 'cargo' && (
                            <div className="relative" ref={cargoDestinationRef}>
                              <label htmlFor="destination" className="block text-gray-700 mb-2 font-semibold flex items-center gap-2">
                                <FontAwesomeIcon icon={faMapMarkerAlt} /> Destination Country
                              </label>
                              <input
                                type="text"
                                id="destination"
                                name="destination"
                                value={formData.destination}
                                onChange={handleChange}
                                onFocus={() => formData.destination && setShowSuggestions(true)}
                                placeholder="Select destination country..."
                                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all ${destinationError ? 'border-red-500' : 'border-gray-300'
                                  }`}
                                required
                              />
                              {destinationError && (
                                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                  <FontAwesomeIcon icon={faExclamationCircle} /> {destinationError}
                                </p>
                              )}
                              {showSuggestions && destinationSuggestions.length > 0 && (
                                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 shadow-lg max-h-60 overflow-y-auto animate-slideDown">
                                  {destinationSuggestions.map((suggestion, index) => (
                                    <div
                                      key={index}
                                      className="px-4 py-3 hover:bg-red-50 cursor-pointer flex justify-between items-center"
                                      onClick={() => handleDestinationSelect(suggestion)}
                                    >
                                      <span>{suggestion.name}</span>
                                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                        {suggestion.type}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {formData.shipmentType === 'haulage' && (
                            <div className="relative" ref={interstateDestinationRef}>
                              <label htmlFor="destination" className="block text-gray-700 mb-2 font-semibold flex items-center gap-2">
                                <FontAwesomeIcon icon={faMapMarkerAlt} /> Destination State
                              </label>
                              <input
                                type="text"
                                id="destination"
                                name="destination"
                                value={formData.destination}
                                onChange={handleChange}
                                onFocus={() => formData.destination && setShowSuggestions(true)}
                                placeholder="Start typing state name..."
                                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all ${destinationError ? 'border-red-500' : 'border-gray-300'
                                  }`}
                                required
                              />
                              {destinationError && (
                                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                  <FontAwesomeIcon icon={faExclamationCircle} /> {destinationError}
                                </p>
                              )}
                              {showSuggestions && destinationSuggestions.length > 0 && (
                                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 shadow-lg max-h-60 overflow-y-auto animate-slideDown">
                                  {destinationSuggestions.map((suggestion, index) => (
                                    <div
                                      key={index}
                                      className="px-4 py-3 hover:bg-red-50 cursor-pointer flex justify-between items-center"
                                      onClick={() => handleDestinationSelect(suggestion)}
                                    >
                                      <span>{suggestion.name}</span>
                                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                        {suggestion.type}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {formData.shipmentType === 'interstate' && (
                          <React.Fragment>
                            <div className="mb-6">
                              <label className="block text-gray-700 mb-3 font-semibold flex items-center gap-2">
                                <FontAwesomeIcon icon={faDoorOpen} /> Delivery Type
                              </label>
                              <div className="flex space-x-4">
                                <div className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${formData.deliveryType === 'pickup'
                                  ? 'border-red-600 bg-red-50'
                                  : 'border-gray-300 hover:border-red-400 hover:bg-red-50'
                                  }`}
                                  onClick={() => setFormData(prev => ({ ...prev, deliveryType: 'pickup' }))}
                                >
                                  <input
                                    type="radio"
                                    id="pickup"
                                    name="deliveryType"
                                    value="pickup"
                                    checked={formData.deliveryType === 'pickup'}
                                    onChange={handleChange}
                                    className="h-5 w-5 text-red-600 focus:ring-red-500 mr-3"
                                  />
                                  <label htmlFor="pickup" className="flex items-center gap-2 cursor-pointer">
                                    <FontAwesomeIcon icon={faWarehouse} /> Pickup
                                  </label>
                                </div>
                                <div className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${formData.deliveryType === 'doorstep'
                                  ? 'border-red-600 bg-red-50'
                                  : 'border-gray-300 hover:border-red-400 hover:bg-red-50'
                                  } ${!doorstepAvailableStates.some(s => s.toUpperCase() === formData.destination.toUpperCase()) ? 'opacity-50' : ''}`}
                                  onClick={() => {
                                    if (doorstepAvailableStates.some(s => s.toUpperCase() === formData.destination.toUpperCase())) {
                                      setFormData(prev => ({ ...prev, deliveryType: 'doorstep' }));
                                    }
                                  }}
                                >
                                  <input
                                    type="radio"
                                    id="doorstep"
                                    name="deliveryType"
                                    value="doorstep"
                                    checked={formData.deliveryType === 'doorstep'}
                                    onChange={handleChange}
                                    disabled={!doorstepAvailableStates.some(s => s.toUpperCase() === formData.destination.toUpperCase())}
                                    className="h-5 w-5 text-red-600 focus:ring-red-500 mr-3"
                                  />
                                  <label htmlFor="doorstep" className={`flex items-center gap-2 cursor-pointer ${!doorstepAvailableStates.some(s => s.toUpperCase() === formData.destination.toUpperCase()) ? 'text-gray-400' : 'text-gray-700'
                                    }`}>
                                    <FontAwesomeIcon icon={faHome} /> Door-step Delivery {
                                      !doorstepAvailableStates.some(s => s.toUpperCase() === formData.destination.toUpperCase())
                                        ? '(Not available)'
                                        : ''
                                    }
                                  </label>
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        )}
                      </React.Fragment>
                    )}

                    <button
                      type="submit"
                      disabled={!formData.shipmentType}
                      className={`w-full py-4 px-6 rounded-xl font-bold transition-all duration-300 flex items-center justify-center gap-2 ${formData.shipmentType
                        ? 'bg-red-600 text-white hover:bg-red-700 hover:shadow-lg transform -translate-y-1'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                      <FontAwesomeIcon icon={faCalculator} />
                      {formData.shipmentType ? 'Get Quote' : 'Select Shipment Type to Continue'}
                    </button>
                  </form>
                </div>

                {/* Info Section */}
                <div className="md:w-1/3 bg-gradient-to-b from-red-50 to-white dark:bg-none dark:bg-black p-8">
                  <h2 className="text-2xl font-bold mb-6 text-white relative pb-3">
                    Our Services
                    <span className="absolute bottom-0 left-0 w-10 h-1 bg-red-600 rounded"></span>
                  </h2>

                  <div className="space-y-4">
                    {renderServiceInfo()}
                  </div>

                  <div className="mt-8 p-4 bg-white rounded-lg border border-red-200 shadow-sm">
                    <h3 className="font-bold text-lg mb-3 text-red-800 flex items-center gap-2">
                      <FontAwesomeIcon icon={faHeadset} /> Need Help?
                    </h3>
                    <p className="text-gray-700 mb-3">
                      Our customer service team is available to assist you with your quote request.
                    </p>
                    <p className="text-gray-700">
                      Call us: <span className="font-semibold">07046674299</span>
                    </p>
                    <p className="text-gray-700">
                      Email: <span className="font-semibold">support@fexlogistics.com.ng</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Quote;